/**
 * SDK Service - Supabase Integration for Widget SDK
 *
 * This service handles all Supabase interactions for the embedded widget SDK.
 * It's separate from mockService.ts to ensure production-ready functionality.
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Widget, Feedback, UserTraits, ProjectStyling } from '../types';
import { sanitizeInput, isValidEmail, isValidComment, isValidBudget } from '../utils/validation';
import { logger } from '../utils/logger';

// Extended Widget type that includes project styling from database
export interface WidgetWithProject extends Widget {
  project: ProjectStyling | null;
}

// Initialize Supabase client for SDK
// Values are injected at build time via tsup.config.ts
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

let supabaseClient: SupabaseClient | null = null;

/**
 * Get or create Supabase client instance
 */
function getSupabaseClient(): SupabaseClient {
  if (!supabaseClient) {
    supabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: false, // SDK doesn't need persistent sessions
        autoRefreshToken: false,
        storageKey: 'verivibe-sdk-auth', // Prevent conflicts with host app
      },
    });
  }
  return supabaseClient;
}

/**
 * User identification for tracking across submissions
 */
interface SDKUser {
  id?: string;
  avatar?: string;
}

let currentUser: SDKUser = {};

/**
 * Identify a user for tracking purposes
 * This allows the widget to track repeat users across submissions
 */
export function identifyUser(userId: string, avatar?: string): void {
  currentUser = {
    id: sanitizeInput(userId, 255),
    avatar: avatar ? sanitizeInput(avatar, 500) : undefined,
  };
}

/**
 * Get current user identification
 */
export function getCurrentUser(): SDKUser {
  return currentUser;
}

/**
 * Clear user identification
 */
export function clearUser(): void {
  currentUser = {};
}

/**
 * Fetch widget configuration from Supabase with project styling
 * This enables live updates to widget styling when project settings change
 */
export async function getWidgetById(widgetId: string): Promise<WidgetWithProject | null> {
  try {
    const supabase = getSupabaseClient();

    // Fetch widget with project styling for automatic propagation
    const { data, error } = await supabase
      .from('widgets')
      .select(
        `
        *,
        project:projects(
          id,
          name,
          brand_color,
          brand_icon,
          widget_padding,
          widget_border_radius,
          custom_css
        )
      `
      )
      .eq('id', widgetId)
      .is('deleted_at', null)
      .single();

    if (error) {
      logger.error('Verivibe SDK: Error fetching widget', error);
      return null;
    }

    if (!data) {
      logger.error('Verivibe SDK: Widget not found');
      return null;
    }

    // Transform database format to WidgetWithProject type
    return {
      id: data.id,
      project_id: data.project_id,
      user_id: data.user_id,
      name: data.name,
      type: data.type,
      config: data.config,
      created_at: data.created_at,
      project: data.project as ProjectStyling | null,
    };
  } catch (error) {
    logger.error('Verivibe SDK: Failed to fetch widget', error);
    return null;
  }
}

/**
 * Collect user traits for analytics
 */
function collectUserTraits(customTraits?: Record<string, unknown>): UserTraits {
  const traits: UserTraits = {
    device: getDeviceType(),
    browser: getBrowserInfo(),
    os: getOSInfo(),
    location: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    pageUrl: window.location.href,
    referrer: document.referrer || undefined,
    customData: customTraits,
  };

  return traits;
}

/**
 * Detect device type
 */
function getDeviceType(): string {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (
    /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      ua
    )
  ) {
    return 'mobile';
  }
  return 'desktop';
}

/**
 * Get browser information
 */
function getBrowserInfo(): string {
  const ua = navigator.userAgent;
  let browser = 'Unknown';

  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('SamsungBrowser')) browser = 'Samsung';
  else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';
  else if (ua.includes('Trident')) browser = 'IE';
  else if (ua.includes('Edge')) browser = 'Edge';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';

  return browser;
}

/**
 * Get OS information
 */
function getOSInfo(): string {
  const ua = navigator.userAgent;
  let os = 'Unknown';

  if (ua.includes('Win')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iOS')) os = 'iOS';

  return os;
}

/**
 * Calculate sentiment from emoji or binary response
 */
function calculateSentiment(
  responseType: string,
  responseValue: string
): {
  sentiment: string;
  score: number;
} {
  if (responseType === 'positive') {
    return { sentiment: 'positive', score: 1.0 };
  }

  if (responseType === 'negative') {
    return { sentiment: 'negative', score: -1.0 };
  }

  if (responseType === 'emoji_value') {
    const emojiScores: Record<string, number> = {
      angry: -1.0,
      sad: -0.5,
      meh: 0.0,
      happy: 0.5,
      love: 1.0,
    };
    const score = emojiScores[responseValue] ?? 0.0;
    const sentiment = score > 0 ? 'positive' : score < 0 ? 'negative' : 'neutral';
    return { sentiment, score };
  }

  return { sentiment: 'neutral', score: 0.0 };
}

/**
 * Upload audio blob to Supabase Storage and return the public URL.
 * Files are stored per-widget under widget-audio/{widgetId}/{timestamp}.webm.
 */
export async function uploadAudio(widgetId: string, blob: Blob): Promise<string> {
  const supabase = getSupabaseClient();
  const fileName = `${widgetId}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.webm`;

  const { data, error } = await supabase.storage
    .from('widget-audio')
    .upload(fileName, blob, { contentType: 'audio/webm', upsert: false });

  if (error) throw new Error(`Audio upload failed: ${error.message}`);

  const {
    data: { publicUrl },
  } = supabase.storage.from('widget-audio').getPublicUrl(data.path);

  return publicUrl;
}

/**
 * Submit feedback to Supabase (main SDK function)
 */
export async function submitVibe(
  widgetId: string,
  projectId: string,
  feedback: {
    response_type: Feedback['response_type'];
    response_value: string | number;
    comment?: string;
    budget?: string;
    email?: string;
    path_taken?: 'positive' | 'negative';
    audio_blob?: Blob;
    is_audio_transcript?: boolean;
  },
  customTraits?: Record<string, unknown>
): Promise<{ success: boolean; error?: string }> {
  try {
    const supabase = getSupabaseClient();

    // Validate and sanitize inputs
    const sanitizedComment = feedback.comment ? sanitizeInput(feedback.comment, 2000) : undefined;

    // Validate comment length
    if (sanitizedComment && !isValidComment(sanitizedComment)) {
      return { success: false, error: 'Comment is too long (max 2000 characters)' };
    }

    // Validate email if provided
    if (feedback.email && !isValidEmail(feedback.email)) {
      return { success: false, error: 'Invalid email address' };
    }

    // Validate budget if provided
    if (feedback.budget && !isValidBudget(feedback.budget)) {
      return { success: false, error: 'Invalid budget value' };
    }

    // Calculate sentiment
    const { sentiment, score } = calculateSentiment(
      feedback.response_type,
      String(feedback.response_value)
    );

    // Collect user traits
    const userTraits = collectUserTraits(customTraits);

    // Get site_id from script tag if available
    const scriptTag = document.querySelector('script[data-site-id]');
    const siteId = scriptTag?.getAttribute('data-site-id') || undefined;

    // Upload audio if provided
    let audioUrl: string | undefined;
    if (feedback.audio_blob) {
      try {
        audioUrl = await uploadAudio(widgetId, feedback.audio_blob);
      } catch (err) {
        logger.error('Verivibe SDK: Audio upload failed', err);
      }
    }

    // Prepare submission data
    const aiMetadata: Record<string, unknown> = {};
    if (audioUrl) {
      aiMetadata.audio_url = audioUrl;
    }

    const submissionData = {
      widget_id: widgetId,
      project_id: projectId,
      site_id: siteId,
      user_id: currentUser.id,
      user_avatar: currentUser.avatar,
      response_type: feedback.response_type,
      response_value: String(feedback.response_value),
      comment: sanitizedComment,
      budget: feedback.budget,
      email: feedback.email,
      path_taken: feedback.path_taken,
      sentiment,
      sentiment_score: score,
      user_traits: userTraits,
      ai_metadata: aiMetadata,
      // True when the SDK fed in a Web Speech API transcript or a server-transcribed blob.
      // Drives the dashboard's voice-origin indicator on the comment row.
      is_audio_transcript: feedback.is_audio_transcript === true,
    };

    // Insert into Supabase
    const { error } = await supabase.from('submissions').insert(submissionData);

    if (error) {
      logger.error('Verivibe SDK: Submission error', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    logger.error('Verivibe SDK: Failed to submit feedback', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Track a widget view (increment view_count atomically via RPC)
 * Fails silently to avoid breaking the widget for analytics tracking
 */
export async function trackWidgetView(widgetId: string): Promise<void> {
  try {
    const supabase = getSupabaseClient();
    await supabase.rpc('increment_widget_view', { p_widget_id: widgetId });
  } catch {
    // Silent fail - don't break widget for tracking
  }
}

/**
 * Export for global SDK API
 */
export const VerivibeSDK = {
  identify: identifyUser,
  clearUser,
  getUser: getCurrentUser,
  submitVibe,
  getWidget: getWidgetById,
};

// Re-export types for SDK consumers
export type { ProjectStyling };
